import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../data";
import { date, time } from "../../lib/format";
import { Fehler, Laden } from "./parts";

/**
 * Instagram-Nachrichten des eigenen Kontos (0223, 0227): Stand der Abfrage, Schalter und die Unterhaltungen mit ihren letzten
 * Nachrichten. Nur lesen; geantwortet wird in Instagram selbst, die automatische Antwort schickt die Serverfunktion.
 */
const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;
type Nachricht = { von_uns: boolean; von: string | null; text: string | null; am: string; automatisch: boolean };
type Daten = {
  stand: { username: string | null; activated_at: string | null; last_sync_at: string | null; last_ok_at: string | null; last_error: string | null } | null;
  abfrage_an: boolean; antwort_an: boolean; antwort_text: string | null;
  unterhaltungen: { id: string; von: string | null; letzte_am: string | null; letzte_von_uns: boolean | null; letzter_text: string | null; antwort_am: string | null; antwort_fehler: string | null; nachrichten: Nachricht[] }[];
};
const wann = (iso: string | null | undefined) => (iso ? `${date(iso)} ${time(iso)}` : "noch nie");

export function Instagram() {
  const [offen, setOffen] = useState<string | null>(null);
  const q = useQuery({ queryKey: ["platform_instagram_dm"], refetchInterval: 60_000, queryFn: async () => parse<Daten>(await api.read("platform_instagram_dm", { p_days: 30 })) });
  const kopf = <><title>Instagram · Plattform · CreatorOffice</title><h1>Instagram-Nachrichten</h1></>;
  if (q.error) return <>{kopf}<Fehler error={q.error} retry={() => void q.refetch()} /></>;
  if (q.isPending || !q.data) return <>{kopf}<Laden /></>;
  const d = q.data;
  return (
    <>
      {kopf}
      <p>Nachrichten an das Instagram-Konto von CreatorOffice{d.stand?.username ? ` (@${d.stand.username})` : ""}. Die Abfrage läuft alle zwei Minuten; auf eine neue Unterhaltung geht einmal die automatische Antwort raus. Geantwortet wird sonst in Instagram selbst.</p>
      <div className="grid three">
        <section className="panel stack"><h2>Abfrage</h2><div className="metric">{d.abfrage_an ? "an" : "aus"}</div><p>Zuletzt ohne Fehler: {wann(d.stand?.last_ok_at)}</p></section>
        <section className="panel stack"><h2>Automatische Antwort</h2><div className="metric">{d.antwort_an ? "an" : "aus"}</div><p>{d.stand?.activated_at ? `Einmal je neuer Unterhaltung. Unterhaltungen von vor dem Einschalten (${wann(d.stand.activated_at)}) bleiben ohne Antwort.` : "Einmal je neuer Unterhaltung. Die Abfrage ist noch nicht eingeschaltet."}</p></section>
        <section className="panel stack"><h2>Unterhaltungen</h2><div className="metric">{d.unterhaltungen.length}</div><p>in den letzten 30 Tagen</p></section>
      </div>
      {d.stand?.last_error && <div className="notice warning" role="alert">Letzter Fehler der Abfrage: {d.stand.last_error}</div>}
      <section className="panel stack" aria-labelledby="ig-text"><h2 id="ig-text">Text der automatischen Antwort</h2><p>{d.antwort_text ?? "–"}</p></section>
      <h2>Unterhaltungen</h2>
      {d.unterhaltungen.length === 0 ? (
        <div className="panel tk-empty"><p>Noch keine Unterhaltung. Meta liefert ohne App-Prüfung nur Unterhaltungen mit Personen, die eine Rolle in der App haben.</p></div>
      ) : (
        <ul className="tk-betrieb-liste">
          {d.unterhaltungen.map((u) => (
            <li key={u.id} className="panel stack">
              <div className="row" style={{ justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <strong>@{u.von ?? "unbekannt"}</strong>
                <small>{wann(u.letzte_am)} · {u.letzte_von_uns ? "zuletzt wir" : "wartet auf Antwort"}{u.antwort_am ? ` · automatische Antwort ${wann(u.antwort_am)}` : ""}</small>
              </div>
              <p>{u.letzter_text ?? "–"}</p>
              {u.antwort_fehler && <p className="tk-error">Automatische Antwort gescheitert: {u.antwort_fehler}</p>}
              <div><button type="button" aria-expanded={offen === u.id} onClick={() => setOffen(offen === u.id ? null : u.id)}>{offen === u.id ? "Verlauf schließen" : `Verlauf zeigen (${u.nachrichten.length})`}</button></div>
              {offen === u.id && (
                <ul className="tk-betrieb-liste">
                  {u.nachrichten.map((m, i) => (
                    <li key={i}><small>{wann(m.am)} · {m.von_uns ? "CreatorOffice" : `@${m.von ?? u.von ?? ""}`}{m.automatisch ? " · automatisch" : ""}</small><br />{m.text ?? "(ohne Text)"}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
